import { useState, useMemo, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, Upload, X } from 'lucide-react'
import type { RiskListItem, RiskTreatmentPlan } from '@common/types'
import { RISK_TREATMENT_PLANS } from '@common/types'
import { useFilters, useSort, type FilterDefinition, type SortColumnDef } from '@frontend/lib/filters'
import { FilterBar } from '@frontend/components/filters/FilterBar'
import { SearchFilter } from '@frontend/components/filters/SearchFilter'
import { MultiSelect } from '@frontend/components/ui/multi-select'
import { fetchRisks } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { formatDate } from '@frontend/lib/utils'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent } from '@frontend/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  SortableTableHead,
} from '@frontend/components/ui/table'
import { Spinner } from '@frontend/components/ui/spinner'
import { Checkbox } from '@frontend/components/ui/checkbox'
import { RiskModal } from '@frontend/components/risks/RiskModal'
import { BulkUploadRisksDialog } from '@frontend/components/risks/BulkUploadRisksDialog'

const TREATMENT_PLAN_OPTIONS = RISK_TREATMENT_PLANS.map((plan) => ({ value: plan, label: plan }))

export function Risks() {
  const { can } = usePermissions()
  const [addRiskOpen, setAddRiskOpen] = useState(false)
  const [selectedRiskIds, setSelectedRiskIds] = useState<Set<string>>(new Set())
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['risks'],
    queryFn: fetchRisks,
  })

  const risks = data?.risks ?? []

  const filterDefinitions = useMemo<FilterDefinition<RiskListItem>[]>(
    () => [
      {
        id: 'search',
        label: 'Search',
        type: 'search',
        match: (risk, value) => {
          const search = value as string
          if (!search) return true
          return risk.name.toLowerCase().includes(search.toLowerCase())
        },
      },
      {
        id: 'treatmentPlan',
        label: 'Treatment',
        type: 'multiselect',
        options: TREATMENT_PLAN_OPTIONS,
        match: (risk, value) => {
          const plans = value as RiskTreatmentPlan[]
          if (plans.length === 0) return true
          return plans.includes(risk.treatmentPlan)
        },
      },
    ],
    []
  )

  const { filteredData, filterState, setFilter, clearAllFilters, activeFilterCount } = useFilters(
    risks,
    filterDefinitions
  )

  const sortColumns = useMemo<SortColumnDef<RiskListItem>[]>(
    () => [
      { id: 'name', accessor: (r) => r.name },
      { id: 'score', accessor: (r) => r.score },
      { id: 'residualScore', accessor: (r) => r.residualScore },
      { id: 'created', accessor: (r) => r.createdAt },
    ],
    []
  )

  const { sortedData, toggleSort, isSorted } = useSort(filteredData, sortColumns, {
    field: 'name',
    direction: 'asc',
  })

  const searchValue = (filterState.search as string) || ''
  const treatmentValue = (filterState.treatmentPlan as string[]) || []

  // Prune selected risk IDs when filter/sort changes hide rows
  const visibleIds = useMemo(() => new Set(sortedData.map((r) => r.id)), [sortedData])
  useEffect(() => {
    setSelectedRiskIds((prev) => {
      const pruned = new Set([...prev].filter((id) => visibleIds.has(id)))
      return pruned.size === prev.size ? prev : pruned
    })
  }, [visibleIds])

  const allVisibleSelected = sortedData.length > 0 && sortedData.every((r) => selectedRiskIds.has(r.id))
  const someSelected = sortedData.some((r) => selectedRiskIds.has(r.id))

  const handleToggleRisk = useCallback((riskId: string) => {
    setSelectedRiskIds((prev) => {
      const next = new Set(prev)
      if (next.has(riskId)) next.delete(riskId)
      else next.add(riskId)
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedRiskIds(new Set())
    } else {
      setSelectedRiskIds(new Set(sortedData.map((r) => r.id)))
    }
  }, [allVisibleSelected, sortedData])

  const riskNamesMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const risk of risks) map.set(risk.id, risk.name)
    return map
  }, [risks])

  const selectedRiskIdsArray = useMemo(() => Array.from(selectedRiskIds), [selectedRiskIds])

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Risks</h1>
            <p className="mt-1 text-muted-foreground">Catalog of risks for client Drata workspaces</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedRiskIds.size > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{selectedRiskIds.size} selected</span>
                <Button onClick={() => setBulkUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-1" />
                  Upload to Client
                </Button>
              </>
            )}
            {can('risks.create') && (
              <Button variant={selectedRiskIds.size > 0 ? 'outline' : 'default'} onClick={() => setAddRiskOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Risk
              </Button>
            )}
          </div>
        </div>

        <Card className="shadow-sm">
          <CardContent className="p-4">
            {!isLoading && !error && (
              <>
                <FilterBar>
                  <SearchFilter
                    value={searchValue}
                    onChange={(value) => setFilter('search', value)}
                    placeholder="Search risks..."
                  />
                  <MultiSelect
                    options={TREATMENT_PLAN_OPTIONS}
                    selected={treatmentValue}
                    onChange={(value) => setFilter('treatmentPlan', value)}
                    placeholder="Treatment..."
                    searchPlaceholder="Search treatments..."
                    emptyMessage="No options found."
                    className="w-44"
                  />
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-9 px-2 text-muted-foreground"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear filters
                    </Button>
                  )}
                </FilterBar>

                <div className="text-sm text-muted-foreground mt-4">
                  {sortedData.length} of {risks.length} risk{risks.length !== 1 ? 's' : ''}
                  {activeFilterCount > 0 && ' (filtered)'}
                </div>
              </>
            )}

            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <p className="text-destructive">Failed to load risks.</p>
                <p className="mt-1 text-sm text-muted-foreground">Please try refreshing the page.</p>
              </div>
            ) : sortedData.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-muted-foreground">
                  {activeFilterCount > 0 ? 'No risks match your filters.' : 'No risks found.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={allVisibleSelected ? true : someSelected ? 'indeterminate' : false}
                          onCheckedChange={handleToggleAll}
                          aria-label="Select all visible risks"
                        />
                      </TableHead>
                      <SortableTableHead
                        className="w-[260px]"
                        sorted={isSorted('name')}
                        onSort={() => toggleSort('name')}
                      >
                        Risk
                      </SortableTableHead>
                      <TableHead className="w-[140px]">Treatment</TableHead>
                      <SortableTableHead
                        className="w-[100px]"
                        sorted={isSorted('score')}
                        onSort={() => toggleSort('score')}
                      >
                        Score
                      </SortableTableHead>
                      <SortableTableHead
                        className="w-[120px]"
                        sorted={isSorted('residualScore')}
                        onSort={() => toggleSort('residualScore')}
                      >
                        Residual
                      </SortableTableHead>
                      <SortableTableHead
                        className="w-[150px]"
                        sorted={isSorted('created')}
                        onSort={() => toggleSort('created')}
                      >
                        Created
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedData.map((risk) => (
                      <TableRow key={risk.id} className="group">
                        <TableCell>
                          <Checkbox
                            checked={selectedRiskIds.has(risk.id)}
                            onCheckedChange={() => handleToggleRisk(risk.id)}
                            aria-label={`Select ${risk.name}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <Link to={`/risks/${risk.id}`} className="hover:underline">
                            {risk.name}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{risk.treatmentPlan}</TableCell>
                        <TableCell>{risk.score}</TableCell>
                        <TableCell>{risk.residualScore}</TableCell>
                        <TableCell>{formatDate(risk.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <RiskModal mode="create" open={addRiskOpen} onOpenChange={setAddRiskOpen} />

      <BulkUploadRisksDialog
        riskIds={selectedRiskIdsArray}
        riskNames={riskNamesMap}
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        onComplete={() => setSelectedRiskIds(new Set())}
      />
    </AppLayout>
  )
}
